import React, { useState, useRef } from 'react';
import Navbar from '../components/Navbar';
import './ApplyJob.css';
import emailjs from '@emailjs/browser';

const ApplyJob = () => {
  const [formData, setFormData] = useState({
    name: '',
    position: 'teaching',
    phone: '',
    pincode: '',
    email: '',
    experience: '',
    resume: null
  });

  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: false,
    message: ''
  });

  const formRef = useRef();

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const validateForm = () => {
    const { name, phone, pincode, email, experience } = formData;
    
    // Name validation
    if (name.length < 2 || name.length > 50) {
      throw new Error('Name must be between 2 and 50 characters');
    }
    
    // Phone validation
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(phone)) {
      throw new Error('Please enter a valid 10-digit phone number');
    }
    
    // Pincode validation
    const pincodeRegex = /^[0-9]{6}$/;
    if (!pincodeRegex.test(pincode)) {
      throw new Error('Please enter a valid 6-digit pincode');
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Please enter a valid email address');
    }
    
    // Experience validation
    if (experience.length < 5 || experience.length > 500) {
      throw new Error('Experience description must be between 5 and 500 characters');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({
      submitting: true,
      success: false,
      error: false,
      message: ''
    });

    try {
      // Validate form data
      validateForm();

      // Send email using EmailJS
      try {
        await emailjs.sendForm(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_JOB_TEMPLATE_ID,
          formRef.current,
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        );
        console.log('Job application email sent successfully');
      } catch (emailError) {
        console.error('EmailJS Error:', emailError);
        throw new Error('Failed to send application: ' + emailError.message);
      }

      // Send data to Google Sheets
      const timestamp = new Date().toLocaleString();
      const sheetData = {
        timestamp,
        name: formData.name,
        position: formData.position,
        phone: formData.phone,
        pincode: formData.pincode,
        email: formData.email,
        experience: formData.experience,
        resumeFileName: formData.resume ? formData.resume.name : 'No file uploaded'
      };

      console.log('Sending job application to Google Sheets:', sheetData);

      // Send to Google Sheets
      try {
        const response = await fetch(process.env.REACT_APP_GOOGLE_SHEETS_JOB_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(sheetData)
        });

        console.log('Google Sheets Response:', response);
      } catch (sheetsError) {
        console.error('Google Sheets Error:', sheetsError);
        throw new Error('Failed to save application: ' + sheetsError.message);
      }

      setFormStatus({
        submitting: false,
        success: true,
        error: false,
        message: 'Thank you for your application! We will review it and get back to you soon.'
      });

      // Reset form
      setFormData({
        name: '',
        position: 'teaching',
        phone: '',
        pincode: '',
        email: '',
        experience: '',
        resume: null
      });

      // Clear success message after 5 seconds
      setTimeout(() => {
        setFormStatus(prev => ({
          ...prev,
          success: false,
          message: ''
        }));
      }, 5000);

    } catch (error) {
      console.error('Form Submission Error:', error);
      setFormStatus({
        submitting: false,
        success: false,
        error: true,
        message: error.message || 'Sorry, there was an error submitting your application. Please try again later.'
      });
    }
  };

  return (
    <div className="apply-job-page">
      <Navbar />
      <div className="apply-job-container">
        <h1>Apply for a Job</h1>
        <p className="subtitle">Join our team and make a difference in education</p>
        
        <form ref={formRef} onSubmit={handleSubmit} className="apply-form">
          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter your full name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email address"
            />
          </div>

          <div className="form-group">
            <label htmlFor="position">Position Type *</label>
            <select
              id="position"
              name="position"
              value={formData.position}
              onChange={handleChange}
              required
            >
              <option value="teaching">Teaching</option>
              <option value="non-teaching">Non-Teaching</option>
              <option value="administration">Administration</option>
              <option value="management">Management</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="Enter your 10-digit phone number"
              pattern="[0-9]{10}"
            />
          </div>

          <div className="form-group">
            <label htmlFor="pincode">Pincode of Residence *</label>
            <input
              type="text"
              id="pincode"
              name="pincode"
              value={formData.pincode}
              onChange={handleChange}
              required
              placeholder="Enter your 6-digit pincode"
              pattern="[0-9]{6}"
            />
          </div>

          <div className="form-group">
            <label htmlFor="experience">Relevant Experience *</label>
            <textarea
              id="experience"
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              required
              placeholder="Describe your relevant experience and why you want to join Biyanis Education Group"
              rows="4"
            />
          </div>

          <div className="form-group">
            <label htmlFor="resume">Upload Resume *</label>
            <input
              type="file"
              id="resume"
              name="resume"
              onChange={handleChange}
              required
              accept=".pdf,.doc,.docx"
            />
            <small>Accepted formats: PDF, DOC, DOCX (Max size: 5MB)</small>
          </div>

          <button 
            type="submit" 
            className="submit-button"
            disabled={formStatus.submitting}
          >
            {formStatus.submitting ? 'Submitting...' : 'Submit Application'}
          </button>

          {formStatus.message && (
            <div className={`form-message ${formStatus.success ? 'success' : 'error'}`}>
              {formStatus.message}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ApplyJob; 